/**
 * Constructing the two things a session is made of.
 *
 * Separate from the store because the persistence codec has to normalise a
 * session read off disk, and it must not import the store to do it — the store
 * reads persisted state at module evaluation, and a cycle there resolves to
 * `undefined` in a way that fails silently on first boot.
 */

import type { ReasonNode, Session } from './types'
import { uid } from './id'
import { clamp } from './geometry'
import { createResearchState } from './research'

/**
 * The one place a ReasonNode is constructed.
 *
 * This function ENUMERATES every field — it deliberately does not spread `p`,
 * so junk from a stored document cannot become part of a node. The cost is that
 * anything it does not name is silently discarded, including on the restore
 * path, which rebuilds every stored node through here.
 *
 * RULE: any field added to `ReasonNode` is added to this function in the same
 * commit, or it will not survive a reload and nothing will tell you.
 */
export function makeNode(p: Partial<ReasonNode> & { label: string; claim: string }): ReasonNode {
  const now = Date.now()
  // A conjecture IS its record. The kind without one would draw the open-
  // proposal glyph over a claim carrying no prediction to test, so it falls back
  // to the ordinary default here — the one constructor — rather than at each
  // path that might build one bare. And a record on any other kind is dropped:
  // the kind is what every reader branches on.
  const conjecture = p.kind === 'conjecture' ? p.conjecture : undefined
  const kind = p.kind === 'conjecture' && !conjecture ? 'mechanism' : (p.kind ?? 'mechanism')
  return {
    id: p.id ?? uid(),
    parentId: p.parentId ?? null,
    bridgeFrom: p.bridgeFrom,
    // The typed edges, beside the tree edge they extend, and enumerated here for
    // the reason the epistemic fields below are: a field the type declares and
    // this omits survives in memory and vanishes on reload.
    dependsOn: p.dependsOn,
    attacks: p.attacks,
    label: p.label,
    claim: p.claim,
    reasoning: p.reasoning ?? '',
    textRetentionVersion: p.textRetentionVersion,
    textTruncations: p.textTruncations,
    // Explicit, never inferred from prose length or from the presence of detail.
    // An elaboration without the kernel state it expands is malformed and is
    // dropped here just as it is on the persistence boundary.
    compact: p.compact === true ? true : undefined,
    elaboration: p.compact === true ? p.elaboration : undefined,
    kind,
    confidence: clamp(p.confidence ?? 0.6),
    novelty: clamp(p.novelty ?? 0.4),
    tension: clamp(p.tension ?? 0.1),
    evidence: p.evidence ?? [],
    nextSteps: p.compact === true ? [] : (p.nextSteps ?? []),
    terminal: p.terminal ?? false,
    terminalReason: p.terminalReason,
    terminalKind: p.terminalKind,
    depth: p.depth ?? 0,
    createdAt: p.createdAt ?? now,
    createdOrdinal: p.createdOrdinal,
    editedAt: p.editedAt,
    reasoningEditedAt: p.reasoningEditedAt,
    // The epistemic fields, in the same order as the type declares them and
    // as the codec validates them. A field named in `ReasonNode` and missing here
    // survives in memory and vanishes on the next reload, silently.
    verification: p.verification,
    verificationHistory: p.verificationHistory,
    evidenceRebases: p.evidenceRebases,
    // The seed's alone, structurally: a survey is of the question, and a claim
    // carrying one would be a record about words that are not the question.
    survey: (p.parentId ?? null) === null ? p.survey : undefined,
    surveyHistory: (p.parentId ?? null) === null ? p.surveyHistory : undefined,
    panel: p.panel,
    audit: p.audit,
    outcome: p.outcome,
    resolvesBy: p.resolvesBy,
    foreignFrom: p.foreignFrom,
    conjecture,
    carriedFrom: p.carriedFrom,
    // On the node and not only on the op: `ops` is sliced to MAX_OPS on the way
    // to disk, so a long exploration would silently lose the ability to say which
    // model produced which half of a mixed graph.
    model: p.model,
    collapsed: p.collapsed ?? false,
    foldAt: p.foldAt,
    // Beside the fold because it is the same kind of state — the reader's own
    // arrangement of the graph — and named here for the reason the note above
    // gives: this object is enumerated, not spread, so a field the type
    // declares and this omits survives in memory and vanishes on reload.
    stretch: p.stretch,
    origin: p.origin ?? 'manual',
    x: 0,
    y: 0,
    angle: 0,
    radius: 0,
    birth: p.birth ?? now,
    lastPulse: p.lastPulse ?? now,
  }
}

export function emptySession(seed = ''): Session {
  const id = uid('s')
  const now = Date.now()
  return {
    id,
    seed,
    research: createResearchState({ id: `research_${id}`, now }),
    createdAt: now,
    nodes: {},
    order: [],
    bridges: [],
    ops: [],
    nextNodeOrdinal: 0,
    verificationArchive: [],
    // Empty, not zeroed: a row appears the first time a model runs a pass here,
    // so an exploration nobody has spent anything on has no ledger rather than a
    // ledger of noughts.
    spend: [],
    // Empty, not absent: the type promises an array, and a restored session
    // handing `undefined` to the first consumer that maps over it throws on the
    // load after the upgrade rather than at the commit that caused it.
    runs: [],
  }
}
